<template>
	<div class="multi-format-keyframe-lane">
		<div
			ref="lane"
			class="multi-format-keyframe-lane__track"
			:class="{ 'is-zoomed': zoomLevel > MIN_ZOOM_LEVEL, 'is-needle-dragging': isNeedleDragging || isTrackDragging }"
			@mousedown="handleTrackMouseDown"
			@touchstart.prevent="handleTrackMouseDown"
			@wheel.prevent="handleWheel"
		>
			<div
				ref="timeline"
				class="multi-format-keyframe-lane__timeline"
				:style="timelineStyle"
			>
				<div class="multi-format-keyframe-lane__scale">
					<span
						v-for="scaleTick in scaleTicks"
						:key="scaleTick.value"
						class="multi-format-keyframe-lane__scale-tick"
						:style="{ left: `${getTimePercentage(scaleTick.value)}%` }"
					>
						{{ scaleTick.label }}
					</span>
				</div>
				<svg
					class="multi-format-keyframe-lane__path"
					preserveAspectRatio="none"
					viewBox="0 0 100 100"
				>
					<line
						class="multi-format-keyframe-lane__path-line"
						x1="0"
						y1="50"
						x2="100"
						y2="50"
					/>
				</svg>
				<button
					type="button"
					class="multi-format-keyframe-lane__grabber multi-format-keyframe-lane__needle -needle"
					:style="needleStyle"
					@mousedown.stop="handleNeedleMouseDown"
					@touchstart.stop.prevent="handleNeedleMouseDown"
				/>
				<div
					class="multi-format-keyframe-lane__trimmed-area"
					:style="trimmedAreaStyle"
				/>
				<button
					type="button"
					class="multi-format-keyframe-lane__grabber -end"
					:style="endHandleStyle"
					@mousedown.stop="handleEndHandleMouseDown"
					@touchstart.stop.prevent="handleEndHandleMouseDown"
				/>
				<button
					v-for="(keyPoint, keyPointIndex) in internalKeyPoints"
					:key="keyPointIndex"
					type="button"
					class="multi-format-keyframe-lane__keyframe"
					:class="{ 'is-selected': keyPointIndex === selectedKeyframeIndex }"
					:style="getKeyframeStyle(keyPoint)"
					@focus="selectKeyframe(keyPointIndex)"
					@pointerdown.stop.prevent="handleKeyframePointerDown($event, keyPointIndex)"
					@mousedown.stop.prevent="handleKeyframeMouseDown(keyPointIndex)"
					@touchstart.stop.prevent="handleKeyframeTouchStart(keyPointIndex)"
				/>
			</div>
		</div>
		<div class="multi-format-keyframe-lane__toolbar">
			<Button
				v-if="selectedKeyframeIndex === null"
				icon="e-add"
				icon-size="sm"
				size="small"
				modifier="primary"
				:theme="$arenaConfig.isLightTheme ? null : 'dark'"
				:reduced-height="true"
				:title="$Helpers.ucfirst($I18n.trans('commons.add'))"
				:disabled="isAddKeyframeDisabled"
				@click="addKeyframe"
			/>
			<Button
				v-else
				icon="e-delete"
				icon-size="sm"
				size="small"
				modifier="danger"
				:theme="$arenaConfig.isLightTheme ? null : 'dark'"
				:reduced-height="true"
				:title="$Helpers.ucfirst($I18n.trans('commons.delete'))"
				:disabled="!canDeleteSelectedKeyframe"
				@click="deleteSelectedKeyframe"
			/>
		</div>
	</div>
</template>

<script>
import Button from '@shared/components/Button';

const MIN_KEYFRAME_GAP_SECONDS = 0.05;
const MIN_ZOOM_LEVEL = 1;
const MAX_ZOOM_LEVEL = 8;
const WHEEL_ZOOM_STEP = 0.0015;
const AUTO_SCROLL_EDGE_PX = 72;
const AUTO_SCROLL_MAX_PX_PER_FRAME = 24;
const TIMELINE_END_PADDING_PX = 17;
const TIMELINE_HORIZONTAL_PADDING_PX = TIMELINE_END_PADDING_PX;

const getClientPosition = (event) => {
	const touch = event.touches?.[0] ?? event.changedTouches?.[0];
	return {
		x: touch?.clientX ?? event.clientX,
	};
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default {
	name:       'MultiFormatKeyframeLane',
	components: { Button },
	props:      {
		keyPoints: {
			type:    Array,
			default: () => [],
		},
		duration: {
			type:    Number,
			default: 0,
		},
		currentTime: {
			type:    Number,
			default: 0,
		},
		currentXLeft: {
			type:    Number,
			default: 0,
		},
		isPlaying: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			MIN_ZOOM_LEVEL,
			selectedKeyframeIndex: null,
			draggedKeyframeIndex:  null,
			maxDuration:           this.duration,
			zoomLevel:             MIN_ZOOM_LEVEL,
			pinchDistance:         null,
			pinchZoomLevel:        MIN_ZOOM_LEVEL,
			pinchCenterTime:       null,
			needleAutoScrollFrame: null,
			needleAutoScrollX:     null,
			isNeedleDragging:      false,
			needleDragTime:        null,
			isTrackDragging:       false,
		};
	},
	computed: {
		internalKeyPoints() {
			return [...this.keyPoints].sort((firstPoint, secondPoint) => firstPoint.t - secondPoint.t);
		},
		formattedDuration() {
			return `${Math.round(this.duration * 10) / 10}s`;
		},
		scaleTicks() {
			const tickStep = this.maxDuration <= 10 ? 1 : 5;
			const ticks = [];

			for (let tickTime = 0; tickTime < this.duration; tickTime += tickStep) {
				ticks.push({
					value: tickTime,
					label: `${tickTime}s`,
				});
			}

			return ticks;
		},
		canDeleteSelectedKeyframe() {
			return this.selectedKeyframeIndex !== null &&
				this.selectedKeyframeIndex > 0 &&
				this.selectedKeyframeIndex < this.internalKeyPoints.length - 1;
		},
		isAddKeyframeDisabled() {
			return this.internalKeyPoints.some(existingKeyPoint => Math.abs(existingKeyPoint.t - this.currentTime) <= MIN_KEYFRAME_GAP_SECONDS);
		},
		timelineStyle() {
			return {
				width: `calc(${this.zoomLevel * 100}% - ${TIMELINE_HORIZONTAL_PADDING_PX}px)`,
			};
		},
		needleStyle() {
			return {
				left: `${this.getTimePercentage(this.needleDragTime ?? this.currentTime)}%`,
			};
		},
		endHandleStyle() {
			return {
				left: `${this.getTimePercentage(this.duration)}%`,
			};
		},
		trimmedAreaStyle() {
			return {
				left: `${this.getTimePercentage(this.duration)}%`,
			};
		},
		minEndTime() {
			if (this.internalKeyPoints.length < 2) {
				return MIN_KEYFRAME_GAP_SECONDS;
			}

			return this.internalKeyPoints[this.internalKeyPoints.length - 2].t;
		},
	},
	watch: {
		duration(duration) {
			this.maxDuration = Math.max(this.maxDuration, duration);
		},
	},
	beforeDestroy() {
		this.removeDragListeners();
		this.stopNeedleAutoScroll();
	},
	methods: {
		getTimePercentage(time) {
			return this.maxDuration > 0 ? (clamp(time, 0, this.maxDuration) / this.maxDuration) * 100 : 0;
		},
		getKeyframeStyle(keyPoint) {
			return {
				left: `${this.getTimePercentage(keyPoint.t)}%`,
				top:  '50%',
			};
		},
		addKeyframe() {
			if (this.isAddKeyframeDisabled) {
				return;
			}

			const keyPoint = {
				...(this.internalKeyPoints[0] ?? {}),
				t:     clamp(this.currentTime, 0, this.duration),
				xLeft: clamp(this.currentXLeft, 0, 1),
			};

			const nextKeyPoints = [...this.internalKeyPoints, keyPoint].sort((firstPoint, secondPoint) => firstPoint.t - secondPoint.t);
			this.selectedKeyframeIndex = nextKeyPoints.findIndex(nextKeyPoint => nextKeyPoint === keyPoint);
			this.emitKeyPoints(nextKeyPoints);
			this.emitSelectedKeyframe();
		},
		deleteSelectedKeyframe() {
			if (!this.canDeleteSelectedKeyframe) {
				return;
			}

			const nextKeyPoints = this.internalKeyPoints.filter((_keyPoint, keyPointIndex) => keyPointIndex !== this.selectedKeyframeIndex);
			this.selectedKeyframeIndex = null;
			this.emitKeyPoints(nextKeyPoints);
			this.emitSelectedKeyframe();
		},
		handleTrackMouseDown(event) {
			if (event.touches?.length > 1) {
				this.startPinchZoom(event);
				return;
			}

			this.clearSelectedKeyframe();
			this.isTrackDragging = true;
			this.needleAutoScrollX = getClientPosition(event).x;
			this.seekNeedleFromClientX(this.needleAutoScrollX);
			this.startNeedleAutoScroll();
			document.addEventListener('mousemove', this.handleTrackDragMove);
			document.addEventListener('mouseup', this.handleTrackDragEnd);
			document.addEventListener('touchmove', this.handleTrackDragMove, { passive: false });
			document.addEventListener('touchend', this.handleTrackDragEnd);
		},
		handleKeyframePointerDown(event, keyPointIndex) {
			this.startKeyframeDrag(keyPointIndex);
			event.currentTarget?.setPointerCapture?.(event.pointerId);
			document.addEventListener('pointermove', this.handleDragMove);
			document.addEventListener('pointerup', this.handleDragEnd);
			document.addEventListener('pointercancel', this.handleDragEnd);
		},
		handleKeyframeMouseDown(keyPointIndex) {
			if (window.PointerEvent) {
				return;
			}

			this.startKeyframeDrag(keyPointIndex);
			document.addEventListener('mousemove', this.handleDragMove);
			document.addEventListener('mouseup', this.handleDragEnd);
		},
		handleKeyframeTouchStart(keyPointIndex) {
			if (window.PointerEvent) {
				return;
			}

			this.startKeyframeDrag(keyPointIndex);
			document.addEventListener('touchmove', this.handleDragMove, { passive: false });
			document.addEventListener('touchend', this.handleDragEnd);
			document.addEventListener('touchcancel', this.handleDragEnd);
		},
		startKeyframeDrag(keyPointIndex) {
			this.selectKeyframe(keyPointIndex);
			this.$emit('seek', this.internalKeyPoints[keyPointIndex].t);

			this.draggedKeyframeIndex = keyPointIndex;
		},
		handleNeedleMouseDown(event) {
			this.clearSelectedKeyframe();
			this.isNeedleDragging = true;
			this.needleAutoScrollX = getClientPosition(event).x;
			this.seekNeedleFromClientX(this.needleAutoScrollX);
			this.startNeedleAutoScroll();
			document.addEventListener('mousemove', this.handleNeedleDragMove);
			document.addEventListener('mouseup', this.handleNeedleDragEnd);
			document.addEventListener('touchmove', this.handleNeedleDragMove, { passive: false });
			document.addEventListener('touchend', this.handleNeedleDragEnd);
		},
		handleEndHandleMouseDown(event) {
			this.clearSelectedKeyframe();
			this.updateDurationFromEvent(event);
			document.addEventListener('mousemove', this.handleEndDragMove);
			document.addEventListener('mouseup', this.handleEndDragEnd);
			document.addEventListener('touchmove', this.handleEndDragMove, { passive: false });
			document.addEventListener('touchend', this.handleEndDragEnd);
		},
		handleTrackDragMove(event) {
			event.preventDefault();
			if (event.touches?.length > 1) {
				this.updatePinchZoom(event);
				return;
			}

			this.needleAutoScrollX = getClientPosition(event).x;
			this.seekNeedleFromClientX(this.needleAutoScrollX);
			this.startNeedleAutoScroll();
		},
		handleTrackDragEnd() {
			this.isTrackDragging = false;
			this.needleDragTime = null;
			this.stopNeedleAutoScroll();
			this.resetPinchZoom();
			this.removeTrackDragListeners();
		},
		handleNeedleDragMove(event) {
			event.preventDefault();
			this.needleAutoScrollX = getClientPosition(event).x;
			this.seekNeedleFromClientX(this.needleAutoScrollX);
			this.startNeedleAutoScroll();
		},
		handleNeedleDragEnd() {
			this.isNeedleDragging = false;
			this.needleDragTime = null;
			this.stopNeedleAutoScroll();
			this.removeNeedleDragListeners();
		},
		handleEndDragMove(event) {
			event.preventDefault();
			this.updateDurationFromEvent(event);
		},
		handleEndDragEnd() {
			this.removeEndDragListeners();
		},
		handleDragMove(event) {
			event.preventDefault();
			this.updateDraggedKeyframe(event);
		},
		handleDragEnd() {
			if (this.draggedKeyframeIndex !== null) {
				this.$emit('seek', this.internalKeyPoints[this.draggedKeyframeIndex].t);
			}

			this.draggedKeyframeIndex = null;
			this.removeDragListeners();
		},
		removeDragListeners() {
			document.removeEventListener('pointermove', this.handleDragMove);
			document.removeEventListener('pointerup', this.handleDragEnd);
			document.removeEventListener('pointercancel', this.handleDragEnd);
			document.removeEventListener('mousemove', this.handleDragMove);
			document.removeEventListener('mouseup', this.handleDragEnd);
			document.removeEventListener('touchmove', this.handleDragMove);
			document.removeEventListener('touchend', this.handleDragEnd);
			document.removeEventListener('touchcancel', this.handleDragEnd);
			this.removeTrackDragListeners();
			this.removeNeedleDragListeners();
			this.removeEndDragListeners();
		},
		removeTrackDragListeners() {
			document.removeEventListener('mousemove', this.handleTrackDragMove);
			document.removeEventListener('mouseup', this.handleTrackDragEnd);
			document.removeEventListener('touchmove', this.handleTrackDragMove);
			document.removeEventListener('touchend', this.handleTrackDragEnd);
		},
		removeNeedleDragListeners() {
			document.removeEventListener('mousemove', this.handleNeedleDragMove);
			document.removeEventListener('mouseup', this.handleNeedleDragEnd);
			document.removeEventListener('touchmove', this.handleNeedleDragMove);
			document.removeEventListener('touchend', this.handleNeedleDragEnd);
		},
		removeEndDragListeners() {
			document.removeEventListener('mousemove', this.handleEndDragMove);
			document.removeEventListener('mouseup', this.handleEndDragEnd);
			document.removeEventListener('touchmove', this.handleEndDragMove);
			document.removeEventListener('touchend', this.handleEndDragEnd);
		},
		handleWheel(event) {
			if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
				this.$refs.lane.scrollLeft += event.deltaX || event.deltaY;
				return;
			}

			this.zoomAtClientX(event.clientX, this.zoomLevel * (1 - (event.deltaY * WHEEL_ZOOM_STEP)));
		},
		startPinchZoom(event) {
			this.clearSelectedKeyframe();
			this.pinchDistance = this.getTouchDistance(event);
			this.pinchZoomLevel = this.zoomLevel;
			this.pinchCenterTime = this.getTimeFromClientX(this.getTouchCenterX(event));
			document.addEventListener('touchmove', this.handleTrackDragMove, { passive: false });
			document.addEventListener('touchend', this.handleTrackDragEnd);
		},
		updatePinchZoom(event) {
			if (!this.pinchDistance) {
				return;
			}

			const nextZoomLevel = this.pinchZoomLevel * (this.getTouchDistance(event) / this.pinchDistance);
			this.setZoomLevel(nextZoomLevel, this.pinchCenterTime, this.getTouchCenterX(event));
		},
		resetPinchZoom() {
			this.pinchDistance = null;
			this.pinchZoomLevel = this.zoomLevel;
			this.pinchCenterTime = null;
		},
		getTouchDistance(event) {
			const [firstTouch, secondTouch] = event.touches;

			if (!firstTouch || !secondTouch) {
				return 0;
			}

			return Math.abs(firstTouch.clientX - secondTouch.clientX);
		},
		getTouchCenterX(event) {
			const [firstTouch, secondTouch] = event.touches;

			if (!firstTouch || !secondTouch) {
				return getClientPosition(event).x;
			}

			return (firstTouch.clientX + secondTouch.clientX) / 2;
		},
		zoomAtClientX(clientX, zoomLevel) {
			this.setZoomLevel(zoomLevel, this.getTimeFromClientX(clientX), clientX);
		},
		setZoomLevel(zoomLevel, anchorTime, anchorClientX = null) {
			const nextZoomLevel = clamp(zoomLevel, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL);
			const lane = this.$refs.lane;

			if (!lane || nextZoomLevel === this.zoomLevel) {
				return;
			}

			const anchorProgress = this.getTimePercentage(anchorTime) / 100;
			const anchorOffset = anchorProgress * lane.clientWidth * nextZoomLevel;
			const laneRect = lane.getBoundingClientRect();
			const anchorClientOffset = anchorClientX === null ? lane.clientWidth / 2 : clamp(anchorClientX - laneRect.left, 0, lane.clientWidth);
			const visibleProgress = lane.clientWidth > 0 ? (lane.scrollLeft + (lane.clientWidth / 2)) / (lane.clientWidth * this.zoomLevel) : 0;

			this.zoomLevel = nextZoomLevel;
			this.$nextTick(() => {
				const nextScrollLeft = Number.isFinite(anchorOffset) ? anchorOffset - anchorClientOffset : (lane.scrollWidth * visibleProgress) - (lane.clientWidth / 2);
				lane.scrollLeft = clamp(nextScrollLeft, 0, lane.scrollWidth - lane.clientWidth);
			});
		},
		startNeedleAutoScroll() {
			const scrollDelta = this.getAutoScrollDelta(this.needleAutoScrollX);
			if (this.needleAutoScrollFrame || scrollDelta === 0) {
				return;
			}

			this.needleAutoScrollFrame = requestAnimationFrame(this.updateNeedleAutoScroll);
		},
		stopNeedleAutoScroll() {
			cancelAnimationFrame(this.needleAutoScrollFrame);
			this.needleAutoScrollFrame = null;
			this.needleAutoScrollX = null;
		},
		updateNeedleAutoScroll() {
			const lane = this.$refs.lane;
			const timeline = this.$refs.timeline;

			if (!lane || !timeline || (!this.isNeedleDragging && !this.isTrackDragging) || this.needleAutoScrollX === null) {
				this.needleAutoScrollFrame = null;
				return;
			}

			const scrollDelta = this.getAutoScrollDelta(this.needleAutoScrollX);

			if (scrollDelta === 0) {
				this.needleAutoScrollFrame = null;
				return;
			}

			const nextScrollLeft = clamp(lane.scrollLeft + scrollDelta, 0, lane.scrollWidth - lane.clientWidth);

			if (nextScrollLeft === lane.scrollLeft) {
				this.needleAutoScrollFrame = null;
				return;
			}

			lane.scrollLeft = nextScrollLeft;
			this.seekNeedleFromTimeProgress(this.getNeedleTimeProgress(this.needleAutoScrollX));
			this.needleAutoScrollFrame = requestAnimationFrame(this.updateNeedleAutoScroll);
		},
		getNeedleTimeProgress(clientX) {
			const timeline = this.$refs.timeline;

			if (!timeline || timeline.offsetWidth <= 0) {
				return 0;
			}

			const rect = timeline.getBoundingClientRect();
			return clamp((clientX - rect.left) / rect.width, 0, 1);
		},
		getAutoScrollDelta(clientX) {
			const lane = this.$refs.lane;

			if (!lane || lane.scrollWidth <= lane.clientWidth) {
				return 0;
			}

			const rect = lane.getBoundingClientRect();
			const leftDistance = clientX - rect.left;
			const rightDistance = rect.right - clientX;

			if (leftDistance < AUTO_SCROLL_EDGE_PX) {
				const edgePressure = clamp((AUTO_SCROLL_EDGE_PX - leftDistance) / AUTO_SCROLL_EDGE_PX, 0, 1);
				return -AUTO_SCROLL_MAX_PX_PER_FRAME * edgePressure;
			}

			if (rightDistance < AUTO_SCROLL_EDGE_PX) {
				const edgePressure = clamp((AUTO_SCROLL_EDGE_PX - rightDistance) / AUTO_SCROLL_EDGE_PX, 0, 1);
				return AUTO_SCROLL_MAX_PX_PER_FRAME * edgePressure;
			}

			return 0;
		},
		updateDraggedKeyframe(event) {
			if (this.draggedKeyframeIndex === null) {
				return;
			}

			const keyPoint = this.getKeyPointFromEvent(event);
			const nextKeyPoints = this.internalKeyPoints.map((existingKeyPoint, keyPointIndex) => {
				if (keyPointIndex !== this.draggedKeyframeIndex) {
					return existingKeyPoint;
				}

				if (keyPointIndex === 0 || keyPointIndex === this.internalKeyPoints.length - 1) {
					return existingKeyPoint;
				}

				const previousTime = this.internalKeyPoints[keyPointIndex - 1].t + MIN_KEYFRAME_GAP_SECONDS;
				const nextTime = this.internalKeyPoints[keyPointIndex + 1].t - MIN_KEYFRAME_GAP_SECONDS;
				return {
					...existingKeyPoint,
					t: clamp(keyPoint.t, previousTime, nextTime),
				};
			});

			this.emitKeyPoints(nextKeyPoints);
			this.$emit('keyframe-drag-seek', nextKeyPoints[this.draggedKeyframeIndex].t);
		},
		updateDurationFromEvent(event) {
			const keyPoint = this.getKeyPointFromEvent(event, this.maxDuration);
			const nextDuration = clamp(keyPoint.t, this.minEndTime, this.maxDuration);
			const lastKeyframeIndex = this.internalKeyPoints.length - 1;
			const nextKeyPoints = this.internalKeyPoints.map((existingKeyPoint, keyPointIndex) => {
				if (keyPointIndex !== lastKeyframeIndex) {
					return existingKeyPoint;
				}

				return {
					...existingKeyPoint,
					t: nextDuration,
				};
			});

			this.$emit('duration-change', nextDuration);
			this.emitKeyPoints(nextKeyPoints);
			this.$emit('seek', Math.min(this.currentTime, nextDuration));
		},
		getKeyPointFromEvent(event, duration = this.maxDuration) {
			const { x } = getClientPosition(event);
			const timeProgress = this.getTimeProgressFromClientX(x);

			return {
				t:     timeProgress * duration,
				xLeft: this.currentXLeft,
			};
		},
		seekFromEvent(event) {
			const { x } = getClientPosition(event);
			this.seekFromClientX(x);
		},
		seekFromClientX(clientX) {
			const keyPoint = this.getKeyPointFromClientX(clientX);
			this.$emit('seek', Math.min(keyPoint.t, this.duration));
		},
		seekFromTimeProgress(timeProgress) {
			this.$emit('seek', Math.min(clamp(timeProgress, 0, 1) * this.maxDuration, this.duration));
		},
		seekNeedleFromClientX(clientX) {
			this.seekNeedleFromTimeProgress(this.getTimeProgressFromClientX(clientX));
		},
		seekNeedleFromTimeProgress(timeProgress) {
			const time = Math.min(clamp(timeProgress, 0, 1) * this.maxDuration, this.duration);
			this.needleDragTime = time;
			this.$emit('seek', time);
		},
		getKeyPointFromClientX(clientX, duration = this.maxDuration) {
			const timeProgress = this.getTimeProgressFromClientX(clientX);

			return {
				t:     timeProgress * duration,
				xLeft: this.currentXLeft,
			};
		},
		getTimeFromClientX(clientX) {
			return this.getTimeProgressFromClientX(clientX) * this.maxDuration;
		},
		getTimeProgressFromClientX(clientX) {
			const timeline = this.$refs.timeline;

			if (!timeline) {
				return 0;
			}

			const rect = timeline.getBoundingClientRect();
			const xInTimeline = clientX - rect.left;

			return clamp(xInTimeline / timeline.offsetWidth, 0, 1);
		},
		emitKeyPoints(keyPoints) {
			this.$emit('change', keyPoints.sort((firstPoint, secondPoint) => firstPoint.t - secondPoint.t));
		},
		emitSelectedKeyframe() {
			this.$emit('select', this.selectedKeyframeIndex);
		},
		clearSelectedKeyframe() {
			this.selectedKeyframeIndex = null;
			this.emitSelectedKeyframe();
		},
		selectKeyframe(keyPointIndex) {
			if (this.selectedKeyframeIndex === keyPointIndex) {
				return;
			}

			this.selectedKeyframeIndex = keyPointIndex;
			this.emitSelectedKeyframe();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-keyframe-lane {
	--keyframe-lane-height-mobile: 100px;
	--keyframe-lane-height-desktop: 100px;

	display: flex;
	gap: $sp2;
	width: 100%;
	align-items: stretch;
	user-select: none;
}

.multi-format-keyframe-lane__toolbar {
	display: flex;
	flex-direction: column;
	gap: $sp3;
	align-self: center;

	@media (pointer: coarse) {
		flex-direction: row;
		justify-self: end;
		padding-left: 0 $sp4;
	}
}

.multi-format-keyframe-lane__track {
	position: relative;
	flex-grow: 1;
	height: var(--keyframe-lane-height-desktop);
	padding: 0 $sp3;
	overflow-x: hidden;
	overflow-y: hidden;
	cursor: crosshair;
	touch-action: none;
	scrollbar-width: none;

	@media (pointer: coarse) {
		height: var(--keyframe-lane-height-mobile);
	}

	&::-webkit-scrollbar {
		display: none;
	}

	&.is-zoomed {
		overflow-x: auto;
	}
}

.multi-format-keyframe-lane__timeline {
	position: relative;
	height: 100%;
	border-radius: 2px;

	&::before {
		content: '';
		position: absolute;
		top: 50%;
		right: 0;
		left: 0;
		height: calc(var(--keyframe-lane-height-desktop) / 2);
		border-radius: 2px;
		background-color: var(--color-grey-800);
		transform: translateY(-50%);
	}

	/* stylelint-disable selector-no-qualifying-type, selector-max-type */
	body.-t-light & {
		&::before {
			background-color: var(--color-grey-950);
		}
	}
	/* stylelint-enable selector-no-qualifying-type, selector-max-type */
}

.multi-format-keyframe-lane__path {
	position: absolute;
	top: 0;
	left: 0;
	display: block;
	width: 100%;
	height: 100%;
	overflow: visible;
	pointer-events: none;
}

.multi-format-keyframe-lane__path-line {
	fill: none;
	stroke: transparent;
	stroke-width: 2;
	vector-effect: non-scaling-stroke;
}

.multi-format-keyframe-lane__keyframe {
	position: absolute;
	z-index: 5; // stylelint-disable-line declaration-property-value-allowed-list
	width: 34px;
	height: 34px;
	padding: 0;
	border: 0;
	background-color: transparent;
	transform: translate(-50%, -50%);
	cursor: grab;
	touch-action: none;

	&::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		width: 8px;
		height: 8px;
		background-color: var(--color-white);
		transform: translate(-50%, -50%) rotate(45deg);

		body.-t-light & { // stylelint-disable-line selector-no-qualifying-type, selector-max-type
			background-color: var(--color-grey-500);
		}
	}

	&:focus,
	&:hover {
		outline: none;
		cursor: grab;
	}

	&.is-selected,
	&:focus,
	&:hover {
		&::before {
			background-color: var(--color-hype-yellow) !important; // stylelint-disable-line
		}
	}

	&:active {
		cursor: grabbing;
	}
}

.multi-format-keyframe-lane__grabber {
	position: absolute;
	top: 0;
	bottom: 0;
	z-index: 1;
	width: 20px;
	padding: 0;
	border: 0;
	background-color: transparent;
	transform: translateX(-50%);
	cursor: ew-resize;

	&.-needle {
		--multi-format-keyframe-lane-grabber-color: var(--color-red);
		bottom: 10px;

		@media (pointer: coarse) {
			bottom: 20px;
		}
	}

	&.-end {
		--multi-format-keyframe-lane-grabber-color: var(--color-hype-yellow);
		z-index: 4; // stylelint-disable-line declaration-property-value-allowed-list
		width: 22px;
		bottom: 26px;

		@media (pointer: coarse) {
			bottom: 24px;
		}
	}

	@media (pointer: coarse) {
		width: 56px;
	}

	&::before {
		content: '';
		position: absolute;
		top: 24px;
		bottom: 0;
		left: 50%;
		width: 1px;
		background-color: var(--multi-format-keyframe-lane-grabber-color);
		transform: translateX(-50%);
	}

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 50%;
		width: 7px;
		height: 23px;
		border-radius: 100px; // stylelint-disable-line sh-waqar/declaration-use-variable -- Grabber head needs to be a pill shape independent of component radius tokens.
		background-color: var(--multi-format-keyframe-lane-grabber-color);
		transform: translateX(-50%);
	}

	&:focus {
		outline: none;
	}

	.multi-format-keyframe-lane__track.is-needle-dragging &.-needle {
		z-index: 6; // stylelint-disable-line declaration-property-value-allowed-list -- Keep the active needle above keyframes while scrubbing.
	}
}

.multi-format-keyframe-lane__trimmed-area {
	position: absolute;
	top: 50%;
	right: 0;
	z-index: 0;
	height: calc(var(--keyframe-lane-height-desktop) / 2);
	border-left-width: 1px;
	border-left-style: solid;
	border-left-color: rgba(255, 255, 255, 0.16); // stylelint-disable-line
	background-color: rgba(0, 0, 0, 0.45); // stylelint-disable-line
	transform: translateY(-50%);
	pointer-events: none;

	@media (pointer: coarse) {
		height: calc(var(--keyframe-lane-height-mobile) / 2);
	}

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type, selector-max-type
		border-left-color: rgba(var(--color-rgb-grey-300), 0.35);
		background-color: rgba(var(--color-rgb-grey-800), 0.7);
	}
}

.multi-format-keyframe-lane__track.is-needle-dragging .multi-format-keyframe-lane__grabber.-end {
	pointer-events: none;
}

.multi-format-keyframe-lane__scale {
	position: relative;
	top: 50%;
	transform: translateY(calc(var(--keyframe-lane-height-desktop) / 2 * -1));
	z-index: 2; // stylelint-disable-line declaration-property-value-allowed-list
	display: block;
	height: 18px;
	color: var(--color-grey-300);
	font-size: 12px; // stylelint-disable-line
	line-height: 1;
	pointer-events: none;

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type, selector-max-type
		color: var(--color-grey-400);
	}
}

.multi-format-keyframe-lane__scale-tick {
	position: absolute;
	top: 0;
	display: block;
	transform: translateX(-50%);
	white-space: nowrap;

	&::after {
		content: '';
		position: absolute;
		top: 13px;
		left: 50%;
		width: 1px;
		height: 5px;
		background-color: var(--color-grey-300);
		transform: translateX(-50%);
	}

	&:first-child {
		transform: none;
	}

	&:last-child {
		transform: translateX(-50%);
	}
}
</style>
